/**
 * Game History Recorder & Reconstruction
 * 
 * Records all game events during play and provides functions to reconstruct
 * the complete game state at any point for the replay feature.
 * 
 * During a live game, only the local player's hand is known. Other players'
 * hands are reconstructed at game end from the cards they played.
 */

import { CardNumber, Contract } from '../types/game';
import { sortHand } from '../utils/cards';
import { Player, ScoreData, GameAction, DeclaredPoignee } from './gameActions';
import { findTrickWinner } from './gameReducer';

// ============================================
// Types
// ============================================

/** A single card play with timing info */
export interface CardPlay {
  player: number;       // Player position (0-based)
  card: CardNumber;
  timestamp: number;    // When the card was played (Date.now())
}

/** A bid with timing info */
export interface BidRecord {
  player: number;
  bid: Contract;
  timestamp: number;
}

/** Complete trick history */
export interface TrickHistory {
  trickNumber: number;  // 1-based
  leader: number;       // Player who led the trick
  cards: CardPlay[];    // Cards played in order
  winner: number;       // Player who won the trick
  /** Poignées declared during this trick (before the player's card) */
  poignees?: DeclaredPoignee[];
}

/** Replay step types */
export type ReplayStepType = 'distribution' | 'chien' | 'ecart' | 'trick';

/** A step in the replay timeline */
export interface ReplayStep {
  type: ReplayStepType;
  trickNumber?: number;       // For trick steps (1-based)
  label: string;              // Display label
}

/** State of the game at a specific replay step */
export interface ReplayStepState {
  /** Cards in each player's hand at this step (position -> sorted cards) */
  hands: Map<number, CardNumber[]>;
  /** The trick being played at this step (if type === 'trick') */
  trickCards: CardPlay[];
  /** The chien cards (visible during chien/ecart steps) */
  chien: CardNumber[];
  /** The ecart cards (visible during ecart step) */
  ecart: CardNumber[];
  /** Poignées to display at this step (before the card is played) */
  poignees: DeclaredPoignee[];
}

/** Complete game history for replay */
export interface GameHistory {
  // Game info
  players: Player[];
  playerCount: number;
  dealer: number;
  taker: number;
  contract: Contract;
  myPosition: number;

  // Cards
  initialHands: Map<number, CardNumber[]>;   // position -> initial hand (sorted)
  chien: CardNumber[];
  ecart: CardNumber[];

  // Events
  bids: BidRecord[];
  tricks: TrickHistory[];
  poignees: DeclaredPoignee[];   // All poignées declared in the game
  score?: ScoreData;

  // Timeline
  steps: ReplayStep[];
}

// ============================================
// GameHistoryRecorder
// ============================================

/**
 * Records game events during play.
 * Call record() for each GameAction processed by the reducer.
 * Call buildHistory() at game end to get the complete reconstructed history.
 */
export class GameHistoryRecorder {
  // Raw recorded data
  private _players: Player[] = [];
  private _playerCount = 4;
  private _dealer = 0;
  private _myPosition = 0;
  private _taker: number | undefined;
  private _contract: Contract = Contract.Pass;

  private _chien: CardNumber[] = [];
  private _ecart: CardNumber[] = [];
  private _ecartRecorded = false;

  private _bids: BidRecord[] = [];

  // Trick recording: we collect cards per trick
  private _currentTrickCards: CardPlay[] = [];
  private _currentTrickLeader: number | undefined;
  private _completedTricks: TrickHistory[] = [];
  private _trickNumber = 0;

  // Poignée recording
  private _poignees: DeclaredPoignee[] = [];
  private _currentTrickPoignees: DeclaredPoignee[] = [];

  private _score: ScoreData | undefined;

  private _isRecording = false;
  private _hasDistribue = false;

  /** Start/reset recording for a new deal */
  reset(): void {
    this._players = [];
    this._playerCount = 4;
    this._dealer = 0;
    this._myPosition = 0;
    this._taker = undefined;
    this._contract = Contract.Pass;
    this._chien = [];
    this._ecart = [];
    this._ecartRecorded = false;
    this._bids = [];
    this._currentTrickCards = [];
    this._currentTrickLeader = undefined;
    this._completedTricks = [];
    this._trickNumber = 0;
    this._poignees = [];
    this._currentTrickPoignees = [];
    this._score = undefined;
    this._isRecording = false;
    this._hasDistribue = false;
  }

  get isRecording(): boolean {
    return this._isRecording;
  }

  get hasDistribue(): boolean {
    return this._hasDistribue;
  }

  /**
   * Record a game action. Should be called for each action processed by the reducer.
   */
  record(action: GameAction, timestamp?: number): void {
    const ts = timestamp ?? Date.now();

    switch (action.type) {
      case 'distribue': {
        this.reset();
        this._isRecording = true;
        this._hasDistribue = true;
        this._dealer = action.data.donneur;
        this._playerCount = action.data.contrats.length || 4;

        // Record bids that already exist (reconnection)
        for (let i = 0; i < action.data.contrats.length; i++) {
          if (action.data.contrats[i] >= 0) {
            this._bids.push({
              player: i,
              bid: action.data.contrats[i] as Contract,
              timestamp: ts,
            });
          }
        }

        // If reconnecting to a game in progress, record existing trick
        if (action.data.pliEnCours && action.data.pliEnCours.length > 0) {
          const nonZeroCards = action.data.pliEnCours.filter(c => c !== 0);
          if (nonZeroCards.length > 0 && action.data.entame !== undefined) {
            this._currentTrickLeader = action.data.entame;
            for (let i = 0; i < nonZeroCards.length; i++) {
              this._currentTrickCards.push({
                player: (action.data.entame + i) % this._playerCount,
                card: nonZeroCards[i] as CardNumber,
                timestamp: ts,
              });
            }
          }
          this._trickNumber = action.data.numPli || 0;
        }

        if (action.data.chien) {
          this._chien = [...action.data.chien] as CardNumber[];
        }
        break;
      }

      case 'joueurs': {
        this._players = action.data.joueurs.map(j => ({
          name: j.pseudo,
          position: j.position,
          isConnected: j.connecte !== false,
        }));
        this._playerCount = this._players.length;
        break;
      }

      case 'infos_table': {
        if (action.data.pseudos) {
          this._players = action.data.pseudos.map((pseudo, idx) => ({
            name: pseudo.replace(/¶/g, ' '),
            position: idx,
            isConnected: action.data.connectes ? action.data.connectes[idx] === '1' : true,
          }));
          this._playerCount = this._players.length;
        }
        break;
      }

      case 'set_position': {
        this._myPosition = action.position;
        break;
      }

      case 'contrat': {
        if (!this._isRecording) return;
        this._bids.push({
          player: Number(action.data.joueur),
          bid: Number(action.data.contrat) as Contract,
          timestamp: ts,
        });
        const bidValue = Number(action.data.contrat);
        if (bidValue > (this._contract || 0)) {
          this._contract = bidValue as Contract;
          this._taker = Number(action.data.joueur);
        }
        break;
      }

      case 'chien': {
        if (!this._isRecording) return;
        this._chien = [...action.data.cartes] as CardNumber[];
        if (action.data.preneur !== undefined) {
          this._taker = action.data.preneur;
        }
        if (action.data.contrat !== undefined) {
          this._contract = action.data.contrat as Contract;
        }
        break;
      }

      case 'ecart': {
        if (!this._isRecording) return;
        if (action.data.ecart && action.data.ecart.length > 0) {
          this._ecart = [...action.data.ecart] as CardNumber[];
          this._ecartRecorded = true;
        }
        break;
      }

      case 'ecart_done': {
        if (!this._isRecording) return;
        this._ecart = [...action.data.cartes] as CardNumber[];
        this._ecartRecorded = true;
        break;
      }

      case 'carte': {
        if (!this._isRecording) return;
        const cardPlayer = Number(action.data.joueur);
        const cardPlayed = Number(action.data.carte) as CardNumber;

        // Set leader if first card
        if (this._currentTrickCards.length === 0) {
          this._currentTrickLeader = cardPlayer;
        }

        this._currentTrickCards.push({
          player: cardPlayer,
          card: cardPlayed,
          timestamp: ts,
        });

        // Check if trick is complete
        if (this._currentTrickCards.length >= this._playerCount) {
          this._completeTrick();
        }
        break;
      }

      case 'fin_pli': {
        if (!this._isRecording) return;
        // If we have cards in the current trick but haven't completed it yet,
        // complete it now with the server-provided winner
        if (this._currentTrickCards.length > 0) {
          this._completeTrick(Number(action.data.gagnant));
        }
        break;
      }

      case 'poignee_announce': {
        if (!this._isRecording) return;
        const poigneeData = action.data as DeclaredPoignee;
        this._poignees.push(poigneeData);
        this._currentTrickPoignees.push(poigneeData);
        break;
      }

      case 'score': {
        if (!this._isRecording) return;
        this._score = action.data;
        break;
      }

      // Ignored action types (delays, timing, etc.)
      default:
        break;
    }
  }

  /** Complete the current trick and add it to completed tricks */
  private _completeTrick(serverWinner?: number): void {
    if (this._currentTrickCards.length === 0) return;

    // Calculate winner
    let winner: number;
    if (serverWinner !== undefined) {
      winner = serverWinner;
    } else {
      // Use the reducer's trick winner logic by building a mini-state
      winner = this._calculateTrickWinner();
    }

    this._trickNumber++;
    this._completedTricks.push({
      trickNumber: this._trickNumber,
      leader: this._currentTrickLeader ?? this._currentTrickCards[0].player,
      cards: [...this._currentTrickCards],
      winner,
      poignees: this._currentTrickPoignees.length > 0 ? [...this._currentTrickPoignees] : undefined,
    });

    // Reset for next trick
    this._currentTrickCards = [];
    this._currentTrickLeader = winner; // Winner leads next trick
    this._currentTrickPoignees = [];
  }

  /** Calculate trick winner from current trick cards */
  private _calculateTrickWinner(): number {
    const cards = this._currentTrickCards.map(cp => cp.card);
    const positions = this._currentTrickCards.map(cp => cp.player);

    // Build a minimal state for findTrickWinner
    const miniState = {
      currentTrick: cards,
      trickPlayerPositions: positions,
      entameur: this._currentTrickLeader ?? positions[0],
      playerCount: this._playerCount,
    } as any;

    return findTrickWinner(miniState);
  }

  /**
   * Build the complete game history from recorded events.
   * Should be called at game end (phase 99999).
   * Returns null if not enough data was recorded.
   */
  buildHistory(fallbackPlayers?: { pseudo: string; position: number }[]): GameHistory | null {
    if (!this._hasDistribue || this._completedTricks.length === 0) {
      return null;
    }

    // Reconstruct all player hands from cards played
    const initialHands = this._reconstructHands();

    // Determine ecart if not explicitly recorded
    const ecart = this._ecartRecorded ? this._ecart : this._inferEcart(initialHands);

    // Build replay steps
    const steps = this._buildSteps();

    // Use recorded players; if empty, use fallback or create placeholders
    let players: Player[];
    if (this._players.length > 0) {
      players = [...this._players];
    } else if (fallbackPlayers && fallbackPlayers.length > 0) {
      players = fallbackPlayers.map(p => ({
        name: p.pseudo,
        position: p.position,
        isConnected: true,
      }));
    } else {
      players = Array.from({ length: this._playerCount }, (_, i) => ({
        name: `Joueur ${i}`,
        position: i,
        isConnected: true,
      }));
    }

    return {
      players,
      playerCount: this._playerCount,
      dealer: this._dealer,
      taker: this._taker ?? 0,
      contract: this._contract,
      myPosition: this._myPosition,
      initialHands,
      chien: [...this._chien],
      ecart: [...ecart],
      bids: [...this._bids],
      tricks: this._completedTricks.map(t => ({ ...t, cards: [...t.cards], poignees: t.poignees ? [...t.poignees] : undefined })),
      poignees: [...this._poignees],
      score: this._score,
      steps,
    };
  }

  /**
   * Reconstruct all players' initial hands from the cards they played.
   * Each player's initial hand = all cards they played across all tricks.
   * For the taker: initial hand (before chien) = cards played + ecart - chien
   */
  private _reconstructHands(): Map<number, CardNumber[]> {
    const hands = new Map<number, CardNumber[]>();

    // Collect all cards played by each player
    for (let i = 0; i < this._playerCount; i++) {
      hands.set(i, []);
    }

    for (const trick of this._completedTricks) {
      for (const play of trick.cards) {
        const hand = hands.get(play.player)!;
        hand.push(play.card);
      }
    }

    // For the taker: add ecart cards and remove chien cards
    // Initial hand (at distribution, before chien) = played cards + ecart - chien
    if (this._taker !== undefined && this._contract <= Contract.Garde) {
      const takerHand = hands.get(this._taker)!;
      // Add ecart cards (they were in the taker's hand after chien)
      if (this._ecartRecorded && this._ecart.length > 0) {
        takerHand.push(...this._ecart);
      }
      // Remove chien cards (they were added to hand, not original)
      if (this._chien.length > 0) {
        for (const chienCard of this._chien) {
          const idx = takerHand.indexOf(chienCard);
          if (idx !== -1) {
            takerHand.splice(idx, 1);
          }
        }
      }
    }

    // Sort each hand
    for (const [pos, hand] of hands) {
      hands.set(pos, sortHand(hand));
    }

    return hands;
  }

  /**
   * Infer ecart cards when they weren't explicitly recorded.
   * Ecart = 78 total cards - all played cards - chien cards still in play
   */
  private _inferEcart(initialHands: Map<number, CardNumber[]>): CardNumber[] {
    const allCards = new Set<number>();
    for (let i = 1; i <= 78; i++) allCards.add(i);

    // Remove all cards in initial hands
    for (const hand of initialHands.values()) {
      for (const card of hand) {
        allCards.delete(card);
      }
    }

    // The remaining cards are in the ecart/chien
    // Remove chien cards that are accounted for
    // What's left is the ecart
    return Array.from(allCards);
  }

  /** Build the list of replay steps */
  private _buildSteps(): ReplayStep[] {
    const steps: ReplayStep[] = [];

    steps.push({ type: 'distribution', label: 'DISTRIB' });

    // Only show chien/ecart for Petite and Garde (not Garde Sans/Contre)
    if (this._chien.length > 0 && this._contract <= 2) {
      steps.push({ type: 'chien', label: 'CHIEN' });
    }

    if ((this._ecart.length > 0 || this._ecartRecorded) && this._contract <= 2) {
      steps.push({ type: 'ecart', label: 'ECART' });
    }

    for (const trick of this._completedTricks) {
      steps.push({
        type: 'trick',
        trickNumber: trick.trickNumber,
        label: `${trick.trickNumber}`,
      });
    }

    return steps;
  }
}

// ============================================
// Replay State Computation
// ============================================

/**
 * Compute the game state at a specific replay step.
 * Returns the cards in each player's hand and the trick being played.
 */
export function computeReplayStepState(
  history: GameHistory,
  step: ReplayStep,
  /** How many cards into the current trick to show (0 = just hands, playerCount = all cards) */
  subStep: number = 0,
): ReplayStepState {
  const hands = new Map<number, CardNumber[]>();

  // Start with initial hands
  for (const [pos, hand] of history.initialHands) {
    hands.set(pos, [...hand]);
  }

  let trickCards: CardPlay[] = [];
  let poignees: DeclaredPoignee[] = [];

  switch (step.type) {
    case 'distribution': {
      // Just show initial hands, no tricks
      break;
    }

    case 'chien': {
      // Show initial hands + chien is visible
      // For the taker, add chien cards to hand (if petite/garde)
      if (history.contract <= Contract.Garde && history.taker !== undefined) {
        const takerHand = hands.get(history.taker)!;
        takerHand.push(...history.chien);
        hands.set(history.taker, sortHand(takerHand));
      }
      break;
    }

    case 'ecart': {
      // Show hands after ecart
      // For the taker: add chien, remove ecart
      if (history.contract <= Contract.Garde && history.taker !== undefined) {
        const takerHand = hands.get(history.taker)!;
        // Add chien
        takerHand.push(...history.chien);
        // Remove ecart
        for (const ecartCard of history.ecart) {
          const idx = takerHand.indexOf(ecartCard);
          if (idx !== -1) takerHand.splice(idx, 1);
        }
        hands.set(history.taker, sortHand(takerHand));
      }
      break;
    }

    case 'trick': {
      const trickNum = step.trickNumber!;

      // Remove cards played in all previous tricks + ecart from hands
      // First: apply chien/ecart to taker's hand
      if (history.contract <= Contract.Garde && history.taker !== undefined) {
        const takerHand = hands.get(history.taker)!;
        takerHand.push(...history.chien);
        for (const ecartCard of history.ecart) {
          const idx = takerHand.indexOf(ecartCard);
          if (idx !== -1) takerHand.splice(idx, 1);
        }
        hands.set(history.taker, sortHand(takerHand));
      }

      // Remove cards from all previous tricks
      for (const trick of history.tricks) {
        if (trick.trickNumber < trickNum) {
          for (const play of trick.cards) {
            const hand = hands.get(play.player)!;
            const idx = hand.indexOf(play.card);
            if (idx !== -1) hand.splice(idx, 1);
          }
        }
      }

      // Current trick: remove cards that have been "played" (up to subStep)
      const currentTrick = history.tricks.find(t => t.trickNumber === trickNum);
      if (currentTrick) {
        const cardsToShow = Math.min(subStep, currentTrick.cards.length);
        for (let i = 0; i < cardsToShow; i++) {
          const play = currentTrick.cards[i];
          const hand = hands.get(play.player)!;
          const idx = hand.indexOf(play.card);
          if (idx !== -1) hand.splice(idx, 1);
        }
        trickCards = currentTrick.cards.slice(0, cardsToShow);
        
        // Show poignées for this trick at subStep 0 (before any card is played)
        if (subStep === 0 && currentTrick.poignees && currentTrick.poignees.length > 0) {
          poignees = currentTrick.poignees;
        }
      }
      break;
    }
  }

  return {
    hands,
    trickCards,
    chien: step.type === 'chien' ? history.chien : [],
    ecart: step.type === 'ecart' ? history.ecart : [],
    poignees,
  };
}

/**
 * Get the number of tricks in the game based on player count.
 * 3 players: 24 tricks, 4 players: 18 tricks, 5 players: 15 tricks (approx)
 */
export function getExpectedTrickCount(playerCount: number): number {
  // 78 cards total. Chien has 6 cards (4p), 6 cards (3p), 3 cards (5p)
  // After ecart: 72 cards to play for 4p (18 tricks), 72 for 3p (24 tricks), 75 for 5p (15 tricks)
  switch (playerCount) {
    case 3: return 24;
    case 5: return 15;
    default: return 18; // 4 players
  }
}
